import type { ReactNode } from 'react';
import type { Room } from '@guest/shared';
import { useHouse } from '../lib/store.js';
import { AppBar } from '../components/Chrome.js';
import { RoomCard } from './Home.js';
import { isOn } from '../lib/entity.js';

const FLOOR_LABELS: Record<Room['floor'], string> = {
  ground: 'Ground floor',
  first: 'First floor',
  second: 'Second floor',
  outside: 'Outside',
};

const FLOOR_ORDER: Room['floor'][] = ['ground', 'first', 'second', 'outside'];

export function Rooms(): ReactNode {
  const { context, states } = useHouse();
  if (!context) return <div className="empty">Loading…</div>;

  const roomIsLit = (room: Room): boolean =>
    room.controls.some((c) => c.group === 'lighting' && isOn(states.get(c.entityId)));

  const byFloor = FLOOR_ORDER.map((floor) => ({
    floor,
    rooms: context.rooms.filter((r) => r.floor === floor),
  })).filter((g) => g.rooms.length > 0);

  return (
    <>
      <AppBar title="Rooms" subtitle={`${context.rooms.length} available to you`} />
      <main className="shell">
        {byFloor.length === 0 && <div className="empty">You have not been given access to any rooms.</div>}
        {byFloor.map(({ floor, rooms }) => (
          <section key={floor}>
            <div className="floor-label">
              <span className="eyebrow">{FLOOR_LABELS[floor]}</span>
            </div>
            <div className="room-grid">
              {rooms.map((room) => (
                <RoomCard key={room.id} room={room} lit={roomIsLit(room)} />
              ))}
            </div>
          </section>
        ))}
      </main>
    </>
  );
}
